// Gastos recurrentes del venue (FRA-356): limpieza general, fumigación,
// renta. Se conceptúan una vez con monto y periodicidad, llevan historial
// de precio (patrón de la Fase 0), y el sistema PROPONE cada ocurrencia
// cuando toca; el venue la confirma y solo entonces se vuelve un costo
// real —con su fecha de pago— en venues/{id}/costos. Una propuesta sin
// confirmar no existe para Movimientos, el neto ni el flujo de caja: la
// renta sube, la fumigación se salta un mes, y un cargo que aparece solo
// ensucia las cifras en silencio.
//
// Colección: venues/{id}/gastosRecurrentes/{rid} con {nombre, categoria,
// monto, periodicidad, inicio, activo, proveedorId?, notas, saltados[]} y
// subcolección costoHistorial (el monto acordado). El costo confirmado
// lleva `origen: "recurrente"`, `recurrenteId` y `periodo` (la clave de la
// ocurrencia), que es lo que evita proponerla dos veces.
//
// Nómina queda fuera de alcance a propósito (se granulariza por empleado
// en otra vuelta).

import {
  arrayUnion,
  collection,
  doc,
  serverTimestamp,
  Timestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { DateTime } from "luxon";
import { db } from "../firebase";
import { asentarPrecioAcordado } from "./precioAcordado";
import { normalizeMoneyInput } from "./money";

const DEFAULT_TZ = "America/Mexico_City";

export const PERIODICIDADES = [
  { value: "semanal", label: "Semanal" },
  { value: "quincenal", label: "Quincenal" },
  { value: "mensual", label: "Mensual" },
  { value: "bimestral", label: "Bimestral" },
  { value: "trimestral", label: "Trimestral" },
  { value: "semestral", label: "Semestral" },
  { value: "anual", label: "Anual" },
];

const MESES_POR_PERIODICIDAD = {
  mensual: 1,
  bimestral: 2,
  trimestral: 3,
  semestral: 6,
  anual: 12,
};

export const periodicidadLabel = (v) =>
  PERIODICIDADES.find((p) => p.value === v)?.label || "Mensual";

const MESES_ES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

const toDateTime = (val, timeZone) => {
  const zone = timeZone || DEFAULT_TZ;
  if (!val) return null;
  let d = null;
  if (typeof val.toDate === "function") d = val.toDate();
  else if (val instanceof Date) d = val;
  else if (typeof val.seconds === "number") d = new Date(val.seconds * 1000);
  else d = new Date(val);
  if (!d || isNaN(d.getTime())) return null;
  return DateTime.fromJSDate(d).setZone(zone).startOf("day");
};

const mediodia = (dt) => dt.set({ hour: 12, minute: 0, second: 0, millisecond: 0 });

const capitalizar = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// Cada ocurrencia tiene una clave estable (lo que se guarda en el costo
// como `periodo`), una fecha de causación (mediodía en la zona del venue)
// y una etiqueta legible.
function ocurrencia(periodicidad, dt) {
  switch (periodicidad) {
    case "semanal":
      return {
        key: `${dt.weekYear}-W${String(dt.weekNumber).padStart(2, "0")}`,
        fecha: mediodia(dt).toJSDate(),
        label: `Semana del ${dt.day} de ${MESES_ES[dt.month - 1]} ${dt.year}`,
      };
    case "quincenal": {
      const q = dt.day <= 15 ? 1 : 2;
      return {
        key: `${dt.year}-${String(dt.month).padStart(2, "0")}-Q${q}`,
        fecha: mediodia(dt).toJSDate(),
        label: `${q === 1 ? "1ª" : "2ª"} quincena de ${MESES_ES[dt.month - 1]} ${dt.year}`,
      };
    }
    case "anual":
      return {
        key: `${dt.year}`,
        fecha: mediodia(dt).toJSDate(),
        label: `${dt.year}`,
      };
    case "bimestral":
    case "trimestral":
    case "semestral":
    case "mensual":
    default:
      return {
        key: `${dt.year}-${String(dt.month).padStart(2, "0")}`,
        fecha: mediodia(dt).toJSDate(),
        label:
          periodicidad && periodicidad !== "mensual"
            ? `${capitalizar(MESES_ES[dt.month - 1])} ${dt.year} (${periodicidadLabel(periodicidad).toLowerCase()})`
            : `${capitalizar(MESES_ES[dt.month - 1])} ${dt.year}`,
      };
  }
}

// Las ocurrencias de un recurrente desde su inicio hasta hoy (inclusive),
// en orden cronológico. `maximo` acota cuántas se generan hacia atrás para
// que un recurrente con inicio viejo no proponga años enteros de golpe.
export function ocurrenciasHasta(recurrente, { hoy = new Date(), timeZone, maximo = 24 } = {}) {
  const inicio = toDateTime(recurrente?.inicio, timeZone);
  const fin = toDateTime(hoy, timeZone);
  if (!inicio || !fin || inicio > fin) return [];
  const periodicidad = recurrente?.periodicidad || "mensual";
  const out = [];
  const vistas = new Set();
  const agregar = (dt) => {
    const o = ocurrencia(periodicidad, dt);
    if (vistas.has(o.key)) return;
    vistas.add(o.key);
    out.push(o);
  };
  if (periodicidad === "semanal") {
    for (let c = inicio, i = 0; c <= fin && i < 1000; c = c.plus({ weeks: 1 }), i += 1) agregar(c);
  } else if (periodicidad === "quincenal") {
    // 1ª quincena → día 16 del mismo mes; 2ª → día 1 del siguiente. La
    // primera ocurrencia respeta el día de inicio dentro de su quincena.
    let c = inicio;
    for (let i = 0; c <= fin && i < 1000; i += 1) {
      agregar(c);
      c = c.day <= 15 ? c.set({ day: 16 }) : c.plus({ months: 1 }).set({ day: 1 });
    }
  } else {
    // plus({months}) desde el inicio respeta el día cuando cabe (31 → 30/28
    // si el mes es más corto) sin acumular el recorte.
    const meses = MESES_POR_PERIODICIDAD[periodicidad] || 1;
    for (let i = 0; i < 1000; i += 1) {
      const c = inicio.plus({ months: meses * i });
      if (c > fin) break;
      agregar(c);
    }
  }
  return out.slice(-maximo);
}

export const montoVigente = (r) => Number(r?.monto) || 0;
export const estaActivo = (r) => !!r && r.activo !== false && !r.archivedAt;

// Piso del calendario vigente (FRA-356). La clave de una ocurrencia sale de
// la periodicidad y del inicio ("2026-09" con mensual, "2026-W38" con
// semanal), así que cambiar cualquiera de los dos re-keya TODO: los costos ya
// confirmados dejan de coincidir y el banner vuelve a proponer meses que ya
// se pagaron. Confirmar ahí cobraba el mismo gasto dos veces.
//
// Al cambiar el programa se guarda hasta dónde llegó el calendario anterior;
// de ahí para atrás ya está contado y no se propone nada. El campo puede no
// existir (recurrentes de antes de este fix, o que nunca cambiaron de
// programa) y entonces no hay piso, que es el comportamiento de siempre.
const despuesDelPiso = (recurrente, ocurrencia) => {
  const piso = toDateTime(recurrente?.programaDesde);
  if (!piso) return true;
  // `toDateTime` ancla al inicio del día y las ocurrencias caen a mediodía,
  // así que hay que comparar contra el FIN del día del piso: si no, la
  // ocurrencia del mismo día que la última confirmada se cuela y es
  // exactamente la que se pagaría dos veces.
  return DateTime.fromJSDate(ocurrencia.fecha) > piso.endOf("day");
};

// La última ocurrencia YA confirmada de un recurrente, como Date. Es el piso
// que se guarda al cambiar el programa.
export function ultimaConfirmada(costos, recurrenteId) {
  let max = null;
  (costos || []).forEach((c) => {
    if (c?.recurrenteId !== recurrenteId) return;
    const d = c.fecha?.toDate ? c.fecha.toDate() : c.fecha ? new Date(c.fecha) : null;
    if (!d || isNaN(d.getTime())) return;
    if (!max || d > max) max = d;
  });
  return max;
}

// Lo que toca confirmar: ocurrencias vencidas que no tienen costo ni fueron
// saltadas. Es lo que el banner de Cuentas enseña. Devuelve
// [{ recurrente, ocurrencia }] ordenado del más viejo al más nuevo.
export function propuestasPendientes({
  recurrentes = [],
  costos = [],
  hoy = new Date(),
  timeZone,
  maximo = 24,
} = {}) {
  const confirmadas = {};
  (costos || []).forEach((c) => {
    if (!c?.recurrenteId || !c?.periodo) return;
    (confirmadas[c.recurrenteId] || (confirmadas[c.recurrenteId] = new Set())).add(c.periodo);
  });
  const out = [];
  (recurrentes || []).filter(estaActivo).forEach((r) => {
    const hechas = confirmadas[r.id] || new Set();
    const saltadas = new Set(Array.isArray(r.saltados) ? r.saltados : []);
    ocurrenciasHasta(r, { hoy, timeZone, maximo }).forEach((o) => {
      if (hechas.has(o.key) || saltadas.has(o.key)) return;
      if (!despuesDelPiso(r, o)) return;
      out.push({ recurrente: r, ocurrencia: o });
    });
  });
  return out.sort((a, b) => a.ocurrencia.fecha - b.ocurrencia.fecha);
}

// Costos que salieron de un recurrente (su historial de ocurrencias).
export const costosDeRecurrente = (costos, recurrenteId) =>
  (costos || [])
    .filter((c) => c?.recurrenteId === recurrenteId)
    .sort((a, b) => String(b.periodo || "").localeCompare(String(a.periodo || "")));

// La próxima ocurrencia después de hoy (para "próximo cargo: 1 de octubre").
export function proximaOcurrencia(recurrente, { hoy = new Date(), timeZone } = {}) {
  const inicio = toDateTime(recurrente?.inicio, timeZone);
  const fin = toDateTime(hoy, timeZone);
  if (!inicio || !fin) return null;
  // Se genera hasta un año adelante y se toma la primera después de hoy.
  const futuro = ocurrenciasHasta(
    recurrente,
    { hoy: fin.plus({ years: 1 }).toJSDate(), timeZone, maximo: 400 }
  );
  return futuro.find((o) => DateTime.fromJSDate(o.fecha) > fin.endOf("day")) || null;
}

// ── Escrituras ───────────────────────────────────────────────────────

export function validarRecurrente(datos = {}) {
  if (!String(datos.nombre || "").trim()) return "Ponle nombre al gasto (Renta, Limpieza general…).";
  const monto = Number(normalizeMoneyInput(String(datos.monto ?? "")));
  if (!(monto > 0)) return "El gasto necesita un monto mayor a cero.";
  if (!PERIODICIDADES.some((p) => p.value === datos.periodicidad)) return "Elige cada cuánto se repite.";
  if (!datos.inicio) return "Indica desde cuándo aplica.";
  return null;
}

const docDesdeDatos = (datos, proveedoresPorId = {}) => {
  const proveedorId = datos.proveedorId || null;
  return {
    nombre: String(datos.nombre || "").trim(),
    categoria: datos.categoria || "Otro",
    monto: Number(normalizeMoneyInput(String(datos.monto ?? ""))) || 0,
    periodicidad: datos.periodicidad || "mensual",
    inicio: datos.inicio instanceof Timestamp ? datos.inicio : Timestamp.fromDate(new Date(datos.inicio)),
    proveedorId,
    proveedorNombre: proveedorId ? proveedoresPorId[proveedorId]?.nombre || datos.proveedorNombre || null : null,
    notas: String(datos.notas || "").trim() || null,
    activo: datos.activo !== false,
  };
};

export async function crearRecurrente({ venueId, datos, user, userInfo, proveedoresPorId = {} }) {
  if (!venueId || !user?.uid) throw new Error("Sin venue o sin usuario");
  const error = validarRecurrente(datos);
  if (error) throw new Error(error);
  const ref = doc(collection(db, "venues", venueId, "gastosRecurrentes"));
  const nuevo = docDesdeDatos(datos, proveedoresPorId);
  const batch = writeBatch(db);
  batch.set(ref, {
    ...nuevo,
    saltados: [],
    // Piso del calendario: nace sin él y solo aparece si alguien cambia la
    // periodicidad o el inicio (ver `despuesDelPiso`).
    programaDesde: null,
    archivedAt: null,
    createdAt: serverTimestamp(),
    createdBy: user.uid,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
  });
  asentarPrecioAcordado(batch, {
    docRef: ref,
    monto: nuevo.monto,
    montoAnterior: 0,
    notas: null,
    user,
    userInfo,
  });
  await batch.commit();
  return ref.id;
}

const millis = (v) => {
  if (!v) return null;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (typeof v.toDate === "function") return v.toDate().getTime();
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d.getTime()) ? null : d.getTime();
};

// Cambió el calendario (no el monto): otra periodicidad u otro inicio. Es lo
// que invalida las claves de las ocurrencias — ver `despuesDelPiso`.
export function cambioDePrograma(recurrente, nuevo) {
  const antesPeriodicidad = recurrente?.periodicidad || "mensual";
  return (
    nuevo.periodicidad !== antesPeriodicidad ||
    millis(nuevo.inicio) !== millis(recurrente?.inicio)
  );
}

// Edición: el monto nuevo asienta su registro en el historial (la renta
// pasó de $30,000 a $33,000 en marzo, y se ve).
//
// `costos` son los del venue: solo se miran para calcular el piso cuando
// cambia el calendario (ver `despuesDelPiso`). No hace falta pasarlos si
// solo se edita el monto, pero el caller ya los tiene suscritos.
export async function actualizarRecurrente({ venueId, recurrente, datos, notas = null, user, userInfo, proveedoresPorId = {}, costos = [] }) {
  if (!venueId || !recurrente?.id || !user?.uid) throw new Error("Sin venue, gasto o usuario");
  const error = validarRecurrente(datos);
  if (error) throw new Error(error);
  const ref = doc(db, "venues", venueId, "gastosRecurrentes", recurrente.id);
  // Editar no cambia si está pausado: el formulario no trae `activo`, y sin
  // esto `docDesdeDatos` lo leía como true y reactivaba en silencio un gasto
  // en pausa (y el banner volvía a proponer los meses pausados).
  const nuevo = docDesdeDatos(
    { ...datos, activo: datos.activo === undefined ? recurrente.activo : datos.activo },
    proveedoresPorId
  );
  const patch = {
    ...nuevo,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
  };
  // Al re-keyar el calendario se sella hasta dónde llegó el anterior, para
  // que lo ya confirmado no se vuelva a proponer bajo las claves nuevas. Sin
  // nada confirmado no hay nada que proteger y el piso se queda en null.
  if (cambioDePrograma(recurrente, nuevo)) {
    const ultima = ultimaConfirmada(costos, recurrente.id);
    patch.programaDesde = ultima ? Timestamp.fromDate(ultima) : null;
  }
  const batch = writeBatch(db);
  batch.update(ref, patch);
  const anterior = montoVigente(recurrente);
  if (nuevo.monto !== anterior) {
    asentarPrecioAcordado(batch, {
      docRef: ref,
      monto: nuevo.monto,
      montoAnterior: anterior,
      notas,
      user,
      userInfo,
    });
  }
  await batch.commit();
}

export async function setRecurrenteActivo({ venueId, recurrenteId, activo, user }) {
  if (!venueId || !recurrenteId || !user?.uid) return;
  await updateDoc(doc(db, "venues", venueId, "gastosRecurrentes", recurrenteId), {
    activo: !!activo,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
  });
}

export async function archivarRecurrente({ venueId, recurrenteId, user }) {
  if (!venueId || !recurrenteId || !user?.uid) return;
  await updateDoc(doc(db, "venues", venueId, "gastosRecurrentes", recurrenteId), {
    activo: false,
    archivedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
  });
}

// Confirmar una ocurrencia = crear el costo real. El monto puede ajustarse
// para esa ocurrencia sin tocar el concepto; `montoOriginal` conserva lo
// que proponía el recurrente. Pagado lleva su fecha de pago; por pagar cae
// en Por pagar hasta cubrirse.
export async function confirmarOcurrencia({
  venueId,
  recurrente,
  ocurrencia,
  monto = null,
  estado = "pagado",
  fechaPago = null,
  metodo = null,
  notas = null,
  user,
}) {
  if (!venueId || !recurrente?.id || !ocurrencia?.key || !user?.uid) {
    throw new Error("Sin venue, gasto, período o usuario");
  }
  const propuesto = montoVigente(recurrente);
  const m = monto == null ? propuesto : Number(normalizeMoneyInput(String(monto))) || 0;
  if (!(m > 0)) throw new Error("El monto debe ser mayor a cero.");
  const pagado = estado === "pagado";
  const fecha = Timestamp.fromDate(ocurrencia.fecha instanceof Date ? ocurrencia.fecha : new Date(ocurrencia.fecha));
  const ref = doc(collection(db, "venues", venueId, "costos"));
  const batch = writeBatch(db);
  batch.set(ref, {
    eventId: null,
    concepto: `${recurrente.nombre} · ${ocurrencia.label}`,
    categoria: recurrente.categoria || "Otro",
    proveedorId: recurrente.proveedorId || null,
    proveedorNombre: recurrente.proveedorNombre || null,
    conceptoId: null,
    conceptoNombre: null,
    monto: m,
    montoOriginal: propuesto,
    fecha,
    estado: pagado ? "pagado" : "por_pagar",
    fechaPago: pagado ? fechaPago || fecha : null,
    origen: "recurrente",
    recurrenteId: recurrente.id,
    periodo: ocurrencia.key,
    metodo: pagado ? metodo || null : null,
    notas: notas || null,
    createdAt: serverTimestamp(),
    createdBy: user.uid,
    updatedAt: null,
    updatedBy: null,
  });
  batch.update(doc(db, "venues", venueId, "gastosRecurrentes", recurrente.id), {
    ultimaConfirmacion: ocurrencia.key,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
  });
  await batch.commit();
  return { costoId: ref.id, monto: m };
}

// Saltar una ocurrencia (la fumigación no se hizo este mes) sin tocar el
// concepto: queda en `saltados` y no se vuelve a proponer.
export async function saltarOcurrencia({ venueId, recurrenteId, periodoKey, user }) {
  if (!venueId || !recurrenteId || !periodoKey || !user?.uid) return;
  await updateDoc(doc(db, "venues", venueId, "gastosRecurrentes", recurrenteId), {
    saltados: arrayUnion(periodoKey),
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
  });
}
